import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { Calendar, MapPin, Users, X } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function Events() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [reg, setReg] = useState({ name: "", email: "", phone: "", company: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/events").then((res) => setEvents(res.data)).catch(() => setEvents([])).finally(() => setLoading(false));
    }, []);

    const openRegister = (ev) => {
        setSelected(ev);
        setReg({ name: "", email: "", phone: "", company: "" });
    };

    const submitRegister = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/events/${selected.id}/register`, reg);
            toast.success(`Registered for ${selected.title}`);
            setEvents((prev) => prev.map((ev) => ev.id === selected.id ? { ...ev, registration_count: (ev.registration_count || 0) + 1 } : ev));
            setSelected(null);
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />
            <main className="flex-1">
                {/* HEADER */}
                <section className="bg-[#0F172A] text-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Federation Events</div>
                        <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight max-w-4xl">
                            Industry forums, expos &amp; networking — open to all members.
                        </h1>
                    </div>
                </section>

                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                    {loading ? (
                        <div className="text-[#64748B]" data-testid="events-loading">Loading events…</div>
                    ) : events.length === 0 ? (
                        <div className="border border-dashed border-[#E2E8F0] p-16 text-center" data-testid="events-empty">
                            <Calendar className="w-10 h-10 mx-auto text-[#64748B] mb-4" />
                            <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-2">No upcoming events</h3>
                            <p className="text-sm text-[#64748B]">Check back soon — new events are announced regularly.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]" data-testid="events-grid">
                            {events.map((ev, idx) => (
                                <article key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-white p-8 flex flex-col">
                                    <div className="flex items-center justify-between mb-6">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{String(idx + 1).padStart(2, "0")} · {ev.event_type}</span>
                                        <span className="text-xs text-[#64748B] tabular">{formatDate(ev.event_date)}</span>
                                    </div>
                                    <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-3 leading-tight">{ev.title}</h3>
                                    <p className="text-sm text-[#64748B] mb-6 line-clamp-3 flex-1">{ev.description}</p>
                                    <div className="space-y-2 mb-6 text-sm text-[#64748B]">
                                        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D4AF37]" />{ev.venue} · {ev.start_time}</div>
                                        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#D4AF37]" />{ev.registration_count || 0} / {ev.capacity} registered</div>
                                    </div>
                                    <button
                                        onClick={() => openRegister(ev)}
                                        data-testid={`register-btn-${ev.id}`}
                                        disabled={(ev.registration_count || 0) >= ev.capacity}
                                        className="w-full px-4 py-3 bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:bg-[#64748B] disabled:cursor-not-allowed"
                                    >
                                        {(ev.registration_count || 0) >= ev.capacity ? "Sold Out" : "Register Now"}
                                    </button>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </main>

            {/* REGISTRATION MODAL */}
            {selected && (
                <div data-testid="register-modal" className="fixed inset-0 z-50 bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Event Registration</div>
                                <h3 className="font-display text-2xl font-bold text-[#0F172A] leading-tight">{selected.title}</h3>
                                <div className="text-xs text-[#64748B] mt-2">{formatDate(selected.event_date)} · {selected.venue}</div>
                            </div>
                            <button onClick={() => setSelected(null)} data-testid="close-register-modal" className="text-[#64748B] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
                        </div>

                        <form onSubmit={submitRegister} className="space-y-4">
                            <RegField label="Name" testId="reg-name" value={reg.name} onChange={(v) => setReg({ ...reg, name: v })} />
                            <RegField label="Email" type="email" testId="reg-email" value={reg.email} onChange={(v) => setReg({ ...reg, email: v })} />
                            <RegField label="Phone" testId="reg-phone" value={reg.phone} onChange={(v) => setReg({ ...reg, phone: v })} />
                            <RegField label="Company" testId="reg-company" value={reg.company} onChange={(v) => setReg({ ...reg, company: v })} />
                            <button
                                type="submit" disabled={submitting}
                                data-testid="submit-registration-btn"
                                className="w-full mt-4 px-6 py-4 bg-[#0F172A] text-white text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#1E293B] transition-colors disabled:opacity-60"
                            >{submitting ? "Submitting…" : "Confirm Registration"}</button>
                        </form>
                    </div>
                </div>
            )}

            <PublicFooter />
        </div>
    );
}

function RegField({ label, value, onChange, testId, type = "text" }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-[0.1em] font-medium text-[#64748B] mb-2">{label} <span className="text-[#DC2626]">*</span></label>
            <input
                type={type} required value={value}
                onChange={(e) => onChange(e.target.value)} data-testid={testId}
                className="w-full px-3 py-3 border border-[#E2E8F0] text-sm text-[#0F172A] focus:outline-none focus:border-[#0F172A]"
            />
        </div>
    );
}
